import { lazyWithRecovery } from '@mathflat/shared/@common/utils/lazyWithRecovery'
import { observer } from 'mobx-react'
import { createBrowserRouter, generatePath, Navigate, redirect } from 'react-router-dom'

import { routeName } from '~/@common/constants'
import { resolveRootRedirect } from '~/@common/integration/safeRedirect'
import { isWorkbookTypeAllowed } from '~/@common/models/(Permission)/permissionUtils'
import authService from '~/@common/services/auth.service'
import { commonRepo } from '~/@common/services/repo.service'
import RouteErrorBoundary from '~/@common/ui/AsyncBoundary/RouteErrorBoundary'
import { getEntrancePage } from '~/@common/utils/getEntrancePage'
import HomePage from '~/@pages/home/Home.page'
import IntegrationDevReset from '~/@pages/integration/IntegrationDevReset.page'
import IntegrationLogoutClear from '~/@pages/integration/IntegrationLogoutClear.page'
import IntegrationProbe from '~/@pages/integration/IntegrationProbe.page'
import IntegrationSync from '~/@pages/integration/IntegrationSync.page'
import LoginPage from '~/@pages/login/Login.page.tsx'
import ServerMaintenance from '~/@pages/serverMaintenance'

import PermissionGuardRoute from './(Route)/PermissionGuardRoute'
import ProtectedRoute from './(Route)/ProtectedRoute'
import App from './App'

// 위에 정적으로 남긴 것은 첫 화면(로그인·홈)과 index.html 인라인 스크립트에 타이밍이 묶인
// integration placeholder, 그리고 다른 청크가 안 올 수 있는 점검 화면이다. 나머지는 진입 시 로드한다.
const LectureLinkPage = lazyWithRecovery(() => import('~/@pages/bridge/LectureLink.page'))
const QRPage = lazyWithRecovery(() => import('~/@pages/bridge/QR.page'))
const QrRoleSelectPage = lazyWithRecovery(() => import('~/@pages/bridge/QrRoleSelect.page'))
const SignatureWorkbookLectureLinkPage = lazyWithRecovery(
  () => import('~/@pages/bridge/SignatureWorkbookLectureLink.page'),
)
const TeacherAppBridgePage = lazyWithRecovery(() => import('~/@pages/bridge/TeacherAppBridge.page'))
const CasScoringPage = lazyWithRecovery(() => import('~/@pages/cas/$assignedId/CasScoring.page'))
const CasListPage = lazyWithRecovery(() => import('~/@pages/cas/CasList.page'))
const GuideVideoPage = lazyWithRecovery(() =>
  import('~/@pages/guide-video/GuideVideo.page').then((m) => ({ default: m.GuideVideoPage })),
)
const AiepBridgePage = lazyWithRecovery(() => import('~/@pages/login/aiep/AiepBridge.page'))
const CreverseBridgePage = lazyWithRecovery(
  () => import('~/@pages/login/creverse/CreverseBridge.page'),
)
const CreverseLoginPage = lazyWithRecovery(
  () => import('~/@pages/login/creverse/CreverseLogin.page'),
)
const GuestLoginPage = lazyWithRecovery(() => import('~/@pages/login/guest/GuestLogin.page'))
const AiOnOAuthCallbackPage = lazyWithRecovery(
  () => import('~/@pages/login/naver-works/AiOnOAuthCallback.page'),
)
const AiOnOAuthLoginPage = lazyWithRecovery(
  () => import('~/@pages/login/naver-works/AiOnOAuthLogin.page'),
)
const LoginFromNaverWorksPage = lazyWithRecovery(
  () => import('~/@pages/login/naver-works/LoginFromNaverWorks.page'),
)
const VineBridgePage = lazyWithRecovery(() => import('~/@pages/login/vine/VineBridge.page'))
const VineLoginPage = lazyWithRecovery(() => import('~/@pages/login/vine/VineLogin.page'))
const NotificationPage = lazyWithRecovery(() => import('~/@pages/notification/Notification.page'))
const AttendanceHistoryPage = lazyWithRecovery(
  () => import('~/@pages/parent/attendance/AttendanceHistory.page'),
)
const ParentNoticePage = lazyWithRecovery(() => import('~/@pages/parent/notice/ParentNotice.page'))
const ParentNoticeDetailPage = lazyWithRecovery(
  () => import('~/@pages/parent/notice/ParentNoticeDetail.page'),
)
const ParentReportPage = lazyWithRecovery(() => import('~/@pages/parent/report/ParentReport.page'))
const ProfilePage = lazyWithRecovery(() => import('~/@pages/profile/Profile.page'))
const QrWorksheetScoringPage = lazyWithRecovery(
  () => import('~/@pages/qr-worksheet/$worksheetId/$dateTime/scoring/QrWorksheetScoring.page'),
)
const ChallengeEntry = lazyWithRecovery(() => import('~/@pages/student/challenge/entry.tsx'))
const ConceptLearningPage = lazyWithRecovery(
  () => import('~/@pages/student/concept-learning/ConceptLearning.page'),
)
const ConceptualLearningPage = lazyWithRecovery(
  () => import('~/@pages/student/conceptual-learning/ConceptualLearning.page'),
)
const LearningProcess = lazyWithRecovery(
  () => import('~/@pages/student/learning-process/LearningProcess.page'),
)
const LectureDetailPage = lazyWithRecovery(
  () => import('~/@pages/student/lecture/$lectureId/LectureDetail.page'),
)
const LectureListPage = lazyWithRecovery(() => import('~/@pages/student/lecture/LectureList.page'))
const SignatureWorkbookLectureListPage = lazyWithRecovery(
  () =>
    import(
      '~/@pages/student/lecture/signature-workbook/$curriculumKey/SignatureWorkbookLectureList.page'
    ),
)
const StudentExamByIdScoringPage = lazyWithRecovery(
  () => import('~/@pages/student-exam/$studentExamId/scoring/StudentExamByIdScoring.page'),
)
const StudentWorkbookDetailPage = lazyWithRecovery(
  () => import('~/@pages/student-workbook/detail/StudentWorkbookDetail.page'),
)
const StudentWorkbookListPage = lazyWithRecovery(
  () => import('~/@pages/student-workbook/list/StudentWorkbookList.page'),
)
const StudentWorkbookViewerPage = lazyWithRecovery(
  () => import('~/@pages/student-workbook/viewer/StudentWorkbookViewer.page'),
)
const EssayWorksheetSolvePage = lazyWithRecovery(
  () => import('~/@pages/student-worksheet/$studentWorksheetId/essay/EssayWorksheetSolve.page'),
)
const StudentWorksheetScoringPage = lazyWithRecovery(
  () =>
    import('~/@pages/student-worksheet/$studentWorksheetId/scoring/StudentWorksheetScoring.page'),
)
const StudentWorksheetDetailPage = lazyWithRecovery(
  () => import('~/@pages/student-worksheet/$studentWorksheetId/StudentWorksheetDetail.page'),
)
const StudentWorksheetListPage = lazyWithRecovery(() =>
  import('~/@pages/student-worksheet/StudentWorksheetList.page').then((m) => ({
    default: m.StudentWorksheetListPage,
  })),
)
const DraggableTestPage = lazyWithRecovery(() => import('~/@pages/test/DraggableTest.page'))

const WorkbookDefaultRedirect = observer(() => {
  const { permission } = commonRepo
  const defaultPath = isWorkbookTypeAllowed(permission, 'CUSTOM_SIGNATURE')
    ? routeName.student.customSignatureStudentWorkbookList
    : isWorkbookTypeAllowed(permission, 'ACADEMY_GROUP')
      ? routeName.student.academyGroupStudentWorkbookList
      : routeName.student.generalStudentWorkbookList

  return <Navigate to={defaultPath} replace />
})

export interface RouteHandle {
  ignoreMobileContainerPadding?: boolean // breakpoints 기준 mobile이면 <Container />에 padding: 0 지정
  ignoreContainerPadding?: boolean // 모든 breakpoint에서 <Container />의 padding, max-width 제거
}

// type issue: https://github.com/remix-run/react-router/issues/10787
export const appRouter: ReturnType<typeof createBrowserRouter> = createBrowserRouter(
  [
    {
      element: <App />,
      ErrorBoundary: RouteErrorBoundary,
      loader: ({ request }) => {
        const safeRedirect = resolveRootRedirect(request.url)
        if (safeRedirect) return redirect(safeRedirect)
        return null
      },
      children: [
        {
          path: 'lecture/:lectureId',
          loader: ({ params }) => {
            return redirect(
              generatePath(routeName.student.lectureDetail, { lectureId: params.lectureId! }),
            )
          },
        },
        {
          path: routeName.login,
          element: <LoginPage />,
        },
        {
          path: routeName.loginCreverse,
          element: <CreverseLoginPage />,
          handle: {
            ignoreContainerPadding: true,
          },
        },
        {
          path: routeName.loginCreverseBridge,
          element: <CreverseBridgePage />,
          handle: {
            ignoreContainerPadding: true,
          },
        },
        {
          path: routeName.loginVine,
          element: <VineLoginPage />,
          handle: {
            ignoreContainerPadding: true,
          },
        },
        {
          path: routeName.loginVineBridge,
          element: <VineBridgePage />,
          handle: {
            ignoreContainerPadding: true,
          },
        },
        {
          // 선생님앱 iframe 브릿지 (ProtectedRoute 밖 — 토큰 없이 접근하는 엔트리 포인트)
          path: routeName.bridge.teacherIframe,
          element: <TeacherAppBridgePage />,
        },
        // b2g 외부 SSO/로그인 엔트리 포인트 (ProtectedRoute 밖 — 로그인 전 단계)
        // ⚠️ 경로값들은 파트너사 시스템에 등록된 redirect_uri/magic URL이므로 절대 변경 금지
        {
          path: routeName.auth.whalespace,
          element: <LoginFromNaverWorksPage />,
        },
        {
          path: routeName.auth.aiOnOAuthLogin,
          element: <AiOnOAuthLoginPage />,
        },
        {
          path: routeName.auth.aiOnOAuthCallback,
          element: <AiOnOAuthCallbackPage />,
        },
        {
          path: routeName.auth.aiepBridge,
          element: <AiepBridgePage />,
        },
        {
          path: routeName.auth.guestLogin,
          element: <GuestLoginPage />,
        },
        {
          // 구 베이직학생앱 호환: 네이티브 앱 딥링크 fallback 브릿지 (ProtectedRoute 밖 — 네이티브 앱 실행이 목적)
          path: routeName.bridge.lecture,
          element: <LectureLinkPage />,
        },
        {
          path: routeName.bridge.signatureWorkbookLecture,
          element: <SignatureWorkbookLectureLinkPage />,
        },
        {
          path: routeName.qrWorksheet.scoring,
          element: (
            <ProtectedRoute>
              <QrWorksheetScoringPage />
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.bridge.qr,
          element: <QRPage />,
        },
        {
          path: routeName.bridge.qrRoleSelect,
          element: <QrRoleSelectPage />,
          handle: {
            ignoreContainerPadding: true,
          },
        },
        // ─── 통합 마이그레이션 placeholder 라우트 ───────────────────────────────
        //   실제 로직은 모두 index.html 인라인 스크립트 (HTML parse 시점에 listener 등록 → race-safe).
        //   본 라우트들은 React 마운트 후 필요없는 API call 이나 로직을 막기 위한 빈 placeholder.
        //   - sync        : (sender→receiver 단방향) receiver 가 띄운 iframe 안에서 REQUEST_SYNC 응답 (storage 통째 송신)
        //   - logoutClear : (양방향) 명시 로그아웃 시 상대 origin 이 띄운 iframe 안에서 LOGOUT_CLEAR 응답
        //   - probe       : sender 가 띄운 invisible iframe 이 자발 PROBE_RESPONSE 송신 (상대방 배포 확인용)
        //   - devReset    : receiver dev panel 이 띄운 iframe 안에서 sender TRANSFERRED marker 제거 (라이브 외 한정)
        {
          path: routeName.integration.sync,
          element: <IntegrationSync />,
        },
        {
          path: routeName.integration.logoutClear,
          element: <IntegrationLogoutClear />,
        },
        {
          path: routeName.integration.probe,
          element: <IntegrationProbe />,
        },
        {
          path: routeName.integration.devReset,
          element: <IntegrationDevReset />,
        },
        {
          path: routeName.guideVideo,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <GuideVideoPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
        },
        {
          path: routeName.notification,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <NotificationPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
        },
        {
          path: '/auth/*',
          element: (
            <ProtectedRoute>
              <Navigate to={getEntrancePage(authService.userType)} />
            </ProtectedRoute>
          ),
        },
        {
          path: routeName.student.defaultPath,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <HomePage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.student.studentWorksheet,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <StudentWorksheetListPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.student.studentWorksheetById,
          element: (
            <ProtectedRoute>
              <StudentWorksheetDetailPage />
            </ProtectedRoute>
          ),
        },
        {
          path: routeName.student.essaySolveByStudentWorksheetId,
          element: (
            <ProtectedRoute>
              <EssayWorksheetSolvePage />
            </ProtectedRoute>
          ),
        },
        {
          path: routeName.student.studentWorksheetScoringById,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <StudentWorksheetScoringPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.student.studentExamScoringById,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <StudentExamByIdScoringPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.student.cas,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <CasListPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.student.casByAssignedId,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <CasScoringPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.student.challenge,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <ChallengeEntry />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.student.studentWorkbook,
          children: [
            {
              index: true,
              element: (
                <ProtectedRoute>
                  <WorkbookDefaultRedirect />
                </ProtectedRoute>
              ),
            },
            {
              path: routeName.student.studentWorkbookList,
              element: (
                <ProtectedRoute>
                  <PermissionGuardRoute>
                    <StudentWorkbookListPage />
                  </PermissionGuardRoute>
                </ProtectedRoute>
              ),
              handle: {
                ignoreMobileContainerPadding: true,
              },
            },

            {
              path: routeName.student.studentWorkbookDetail,
              element: (
                <ProtectedRoute>
                  <PermissionGuardRoute>
                    <StudentWorkbookDetailPage />
                  </PermissionGuardRoute>
                </ProtectedRoute>
              ),
              handle: {
                ignoreMobileContainerPadding: true,
              },
            },
            {
              path: routeName.student.studentWorkbookViewer,
              element: (
                <ProtectedRoute>
                  <PermissionGuardRoute>
                    <StudentWorkbookViewerPage />
                  </PermissionGuardRoute>
                </ProtectedRoute>
              ),
              handle: {
                ignoreMobileContainerPadding: true,
              },
            },
            {
              path: '*',
              element: (
                <ProtectedRoute>
                  <Navigate to={routeName.student.generalStudentWorkbookList} replace />
                </ProtectedRoute>
              ),
            },
          ],
        },
        {
          path: routeName.student.lecture,
          children: [
            { index: true, element: <Navigate to={routeName.student.lectureList} /> },
            {
              path: routeName.student.lectureList,
              element: (
                <ProtectedRoute>
                  <PermissionGuardRoute>
                    <LectureListPage />
                  </PermissionGuardRoute>
                </ProtectedRoute>
              ),
            },
            {
              path: routeName.student.assignedLectureDetail,
              element: (
                <ProtectedRoute>
                  <PermissionGuardRoute>
                    <LectureDetailPage />
                  </PermissionGuardRoute>
                </ProtectedRoute>
              ),
              handle: {
                ignoreMobileContainerPadding: true,
              },
            },
            {
              path: routeName.student.lectureDetail,
              element: (
                <ProtectedRoute>
                  <PermissionGuardRoute>
                    <LectureDetailPage />
                  </PermissionGuardRoute>
                </ProtectedRoute>
              ),
              handle: {
                ignoreMobileContainerPadding: true,
              },
            },
            {
              path: routeName.student.signatureWorkbookLectureList,
              element: (
                <ProtectedRoute>
                  <PermissionGuardRoute>
                    <SignatureWorkbookLectureListPage />
                  </PermissionGuardRoute>
                </ProtectedRoute>
              ),
            },
          ],
        },
        {
          path: routeName.student.conceptLearning, // 삭제 예정 (진입 시 conceptualLearning으로 redirect)
          element: (
            <ProtectedRoute>
              <ConceptLearningPage />
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.student.conceptualLearning,
          element: (
            <ProtectedRoute>
              <ConceptualLearningPage />
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.profile,
          element: (
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          ),
        },
        {
          path: routeName.parent.defaultPath,
          element: (
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.parent.attendance,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <AttendanceHistoryPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreContainerPadding: true,
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.parent.report,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <ParentReportPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
        },
        {
          path: routeName.parent.notice,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <ParentNoticePage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
        },
        {
          path: routeName.parent.noticeDetail,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <ParentNoticeDetailPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.parent.studentWorksheetScoringById,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <StudentWorksheetScoringPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.parent.essayByStudentWorksheetId,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <EssayWorksheetSolvePage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
        },
        {
          path: routeName.parent.studentExamScoringById,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <StudentExamByIdScoringPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.parent.casByAssignedId,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <CasScoringPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.parent.studentWorkbookViewer,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <StudentWorkbookViewerPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.parent.studentWorkbookDetail,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <StudentWorkbookDetailPage />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.student.studentLearningProcess,
          element: (
            <ProtectedRoute>
              <PermissionGuardRoute>
                <LearningProcess />
              </PermissionGuardRoute>
            </ProtectedRoute>
          ),
          handle: {
            ignoreMobileContainerPadding: true,
          },
        },
        {
          path: routeName.serverMaintenance,
          element: <ServerMaintenance />,
        },
        {
          path: '/test/draggable',
          element: <DraggableTestPage />,
        },
        // 매칭되지 않는 모든 경로(/, 오타, 삭제된 경로 등) catch-all
        {
          path: '*',
          loader: () => {
            if (authService.isLoggedIn) {
              return redirect(getEntrancePage(authService.userType))
            }
            return redirect(routeName.login)
          },
        },
      ],
    },
  ],
  {
    future: {
      v7_normalizeFormMethod: true,
      v7_fetcherPersist: true,
    },
  },
)

export type AppRouter = typeof appRouter
